import { DEFAULT_TENANT_TERMINOLOGY, mergeTenantTerminology } from './tenantTerminology';

export const DIRECTOR_SHELL_ROLES = new Set(['director', 'admin', 'super_admin', 'platform_admin']);

// Users whose account requires a linked manager record in the operational roster
export const MANAGER_SCOPED_ROLES = new Set(['ops_manager', 'airport_manager']);

// Users eligible to receive delegated approval authority
export const DELEGATE_ELIGIBLE_ROLES = new Set(['airport_manager', 'director']);

function buildRoleLabels(terminology = DEFAULT_TENANT_TERMINOLOGY) {
  const resolved = mergeTenantTerminology(terminology);
  return {
    director: 'Director',
    admin: 'Administrator',
    // Workforce People Model Phase 3.1 — UI label aligned with the
    // manager_role_key='ops_manager' / users.role='ops_manager' schema
    // values. The enum keys are unchanged.
    ops_manager: resolved.people.manager,
    airport_manager: `${resolved.organization.location} Manager`,
    supervisor: 'Supervisor',
    employee: resolved.people.employee,
    super_admin: 'Platform Administrator',
    platform_admin: 'Platform Administrator',
    super_user: 'Super User',
    user: 'User'
  };
}

const ROLE_LABELS = buildRoleLabels();

export function formatRoleLabel(roleKey, terminology = DEFAULT_TENANT_TERMINOLOGY) {
  if (!roleKey) return '';
  const labels = terminology === DEFAULT_TENANT_TERMINOLOGY ? ROLE_LABELS : buildRoleLabels(terminology);
  if (labels[roleKey]) return labels[roleKey];
  return String(roleKey)
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

/**
 * Universal three-tier model. Job titles are stored separately and displayed
 * for context only — they do not drive logic or access control.
 *
 * super_user  → full platform access (maps to 'director' internally)
 * admin       → governance access   (maps to 'admin' internally)
 * user        → scoped access       (maps to 'ops_manager' or 'airport_manager'
 *               depending on assignedLocationIds / permissions)
 */
export const TIER_SUPER_USER = 'super_user';
export const TIER_ADMIN = 'admin';
export const TIER_USER = 'user';

export const WORKSPACE_KEYS = {
  OPS: 'ops',
  AIRPORT: 'airport',
  DIRECTOR: 'director',
  ADMIN: 'admin'
};

const LEGACY_ROLE_MAP = {
  ops_admin: 'director',
  location_admin: 'airport_manager',
  manager: 'ops_manager',
  super_user: 'director'
};

const WORKSPACE_BY_ROLE = {
  ops_manager: WORKSPACE_KEYS.OPS,
  airport_manager: WORKSPACE_KEYS.AIRPORT,
  director: WORKSPACE_KEYS.DIRECTOR,
  admin: WORKSPACE_KEYS.ADMIN,
  super_admin: WORKSPACE_KEYS.ADMIN,
  platform_admin: WORKSPACE_KEYS.ADMIN
};

export const APP_ROUTE_META = {
  '/time-clock': {
    title: 'Time Clock',
    category: 'Self Service',
    description: 'Clock in or out and review your recent time entries.'
  },
  '/timecards': {
    title: 'Timecards',
    category: 'Workforce',
    moduleId: 'workforce',
    description: 'Review employee punches in your authorized locations and make auditable corrections.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/timekeeping/reports': {
    title: 'Recorded Hours', category: 'Workforce', moduleId: 'workforce',
    description: 'Review operational recorded hours from authoritative completed punches.',
    requiredPermission: 'employees.read', fallbackPermissions: ['manage_users']
  },
  '/attendance': {
    title: 'Attendance', category: 'Workforce', moduleId: 'workforce',
    description: 'Review server-interpreted schedule and punch facts for authorized locations.',
    requiredPermission: 'employees.read', fallbackPermissions: ['manage_users']
  },
  '/setup': {
    title: 'Setup Wizard',
    category: 'Starter Mode',
    description: 'A guided company setup path for getting locations, team, KPIs, and first goals ready quickly.'
  },
  '/dashboard-preview': {
    title: 'Dashboard Preview',
    category: 'Starter Mode',
    description: 'Preview the private company workspace before launching the full Executive Snapshot.'
  },
  '/executive': {
    title: 'Executive Snapshot',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Thirty-second leadership view of goal attainment, attendance health, and the decisions waiting in your queue.'
  },
  '/alerts': {
    title: 'Alerts Center',
    category: 'Command Center',
    moduleId: 'notifications',
    description: 'Proactive signals ranked by severity — KPIs behind goal, missing actuals, aging approvals, and attendance risk, with wins tucked at the bottom.'
  },
  '/notifications': {
    title: 'Notifications',
    category: 'Command Center',
    moduleId: 'notifications',
    description: 'Workforce compliance notifications — expiring documents, vacation requests, call-outs, and integrity issues anchored to your tenant.'
  },
  '/today': {
    title: 'Today',
    category: 'Command Center',
    moduleId: 'tasks',
    description: 'Daily Focus Queue of ranked work already waiting in approvals, KPIs, actuals, attendance, and action items.'
  },
  '/my-dashboard': {
    title: 'My Dashboard',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Review your current performance, requests, and scorecard context without leaving your assigned operational scope.'
  },
  '/station-dashboard': {
    title: 'Location Overview',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Monitor the current location view, open requests, and operational signals tied to your assigned location.'
  },
  '/station-summary': {
    title: 'Location Summary',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Compare your work to the broader location view without exposing unrestricted peer workflows.'
  },
  '/ops-managers': {
    title: 'Team Directory',
    category: 'Requests & Calendar',
    moduleId: 'workforce',
    description: 'Review the location team directory, request activity, and current operational workload across visible managers.'
  },
  '/director-dashboard': {
    title: 'Command Center',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Monitor location performance, benchmark risk, and executive signals across every location in scope.'
  },
  '/requests-calendar': {
    title: 'Requests & Calendar',
    category: 'Requests & Calendar',
    moduleId: 'requests',
    description: 'Review approvals, request history, and shared time-off visibility without leaving the operational workflow.'
  },
  '/approvals': {
    title: 'Approval Queue',
    category: 'Requests & Calendar',
    moduleId: 'requests',
    description: 'Route alias for the requests workflow used by alerts and executive action CTAs.'
  },
  '/goals': {
    title: 'Goals & Performance',
    category: 'Goals & Performance',
    moduleId: 'goals',
    description: 'Manage goals, allocations, actuals, variance, and KPI status in one shared performance workspace.'
  },
  '/goals/plan': {
    title: 'Goal Plan',
    category: 'Goals & Performance',
    moduleId: 'goals',
    description: 'Plan location-level goals in a fast spreadsheet-style workspace across locations and KPIs.',
    requiredPermission: 'manage_goals'
  },
  '/location-scorecard': {
    title: 'Location Performance Scorecard',
    category: 'Goals & Performance',
    moduleId: 'scorecards',
    description: 'Review the live location scorecard in a dedicated dashboard surface without the extra Command Center admin chrome.'
  },
  '/manager-performance': {
    title: 'Manager Performance',
    category: 'Command Center',
    moduleId: 'dashboards',
    description: 'Route alias for the role-safe performance and attendance workflow used by alerts and executive action CTAs.'
  },
  '/allocations': {
    title: 'Goals & Performance',
    category: 'Goals & Performance',
    moduleId: 'goals',
    description: 'Legacy route for the unified Goals & Performance workspace focused on allocation actions.',
    requiredPermission: 'manage_goals'
  },
  '/actuals': {
    title: 'Goals & Performance',
    category: 'Goals & Performance',
    moduleId: 'goals',
    description: 'Legacy route for the unified Goals & Performance workspace focused on actual-entry actions.',
    requiredPermission: 'update_all_scorecards',
    directorShellBypass: true
  },
  '/kpis': {
    title: 'KPI Definitions',
    category: 'Goals & Performance',
    moduleId: 'scorecards',
    description: 'Configure metric definitions, units, and comparison behavior without changing live performance results.',
    requiredPermission: 'manage_kpis'
  },
  '/locations': {
    title: 'Locations',
    category: 'Admin & Settings',
    moduleId: 'administration',
    description: 'Manage active locations and their operational setup while preserving historical reporting context.',
    requiredPermission: 'manage_locations'
  },
  '/location-setup': {
    title: 'Location Setup',
    category: 'Admin & Settings',
    moduleId: 'administration',
    description: 'Complete starter setup for a newly added location before activating it for dashboards.',
    requiredPermission: 'manage_locations'
  },
  '/managers': {
    title: 'Leadership Directory',
    category: 'Admin & Settings',
    moduleId: 'workforce',
    description: 'Maintain leadership records, status, and hire dates used throughout scorecards and vacation workflows.'
  },
  '/employees': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Search the workforce, select a person, and manage profile, contact, access, documents, and certifications from one hub.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/employees/profiles': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Deep link into the unified People & Talent workspace for employee profiles.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/employees/documents': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'documents',
    description: 'Deep link into the unified People & Talent workspace for employee documents.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/employees/expiring': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'documents',
    description: 'Deep link into the unified People & Talent workspace for expiring certifications.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/employees/profile/:managerId': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Open a selected employee inside the unified People & Talent workspace.'
  },
  '/employees/profile/by-profile/:employeeProfileId': {
    title: 'People & Talent',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Open a selected employee profile inside the canonical Person Workspace path.'
  },
  // Workforce Hub W1 — canonical URLs. Both /workforce and the legacy
  // /employees family render the same workspace; this entry registers
  // /workforce with APP_ROUTE_META so the route-guard allowlist in
  // App.js (Object.keys(APP_ROUTE_META)) accepts it without bouncing
  // operators back to /today.
  '/workforce': {
    title: 'Workforce',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Canonical Workforce workspace — search the workforce, select a person, and manage profile, contact, access, documents, and certifications from one hub.',
    requiredPermission: 'employees.read',
    fallbackPermissions: ['manage_users']
  },
  '/workforce/employees/:employeeProfileId': {
    title: 'Workforce',
    category: 'People & Talent',
    moduleId: 'workforce',
    description: 'Canonical deep link into the Workforce workspace for a selected employee.'
  },
  '/users': {
    title: 'Users & Access',
    category: 'Admin & Settings',
    moduleId: 'administration',
    description: 'Manage user access, password resets, and account status with clear, role-safe administration.',
    requiredPermission: 'manage_users'
  },
  '/timekeeping/admin': {
    title: 'Timekeeping Administration',
    category: 'Admin & Settings',
    moduleId: 'administration',
    description: 'Configure the company payroll calendar, employee kiosk PINs, shared devices, mobile punch availability, and location readiness.',
    requiredPermission: 'manage_users'
  },
  '/policies': {
    title: 'Policy Management',
    category: 'Admin & Settings',
    moduleId: 'requests',
    description: 'Manage blackout periods and coverage rules for future requests without rewriting past operational history.',
    requiredPermission: 'manage_policies'
  },
  '/integrations': {
    title: 'Integrations',
    category: 'Admin & Settings',
    moduleId: 'integrations',
    description: 'Configure external operational channels like EZ Texting while keeping event intake auditable and separate from manual entry.',
    requiredPermission: 'manage_users'
  },
  '/audit': {
    title: 'Data Management & Audit',
    category: 'Data Management & Audit',
    moduleId: 'audits',
    description: 'Centralize CSV import/export actions and review administrative history in one audit-safe workspace.'
  },
  '/admin-workspace': {
    title: 'Governance Workspace',
    category: 'Governance Workspace',
    moduleId: 'administration',
    description: 'Manage platform governance, role assignments, ownership structure, and future KPI configuration without entering the operational command center.'
  },
  '/admin-users': {
    title: 'Users & Roles',
    category: 'Governance Controls',
    moduleId: 'administration',
    description: 'Manage user access, role assignments, and account scope from the governance workspace.',
    requiredPermission: 'manage_users'
  },
  '/admin-locations': {
    title: 'Location Ownership',
    category: 'Governance Controls',
    moduleId: 'administration',
    description: 'Review location setup and maintain primary Director ownership without changing operational workflows.',
    requiredPermission: 'manage_locations'
  },
  '/admin-delegations': {
    title: 'Delegations',
    category: 'Governance Controls',
    moduleId: 'administration',
    description: 'Review temporary approval coverage, location-scoped delegation windows, and active backup permissions.',
    requiredPermission: 'manage_users'
  },
  '/admin-kpis': {
    title: 'KPI Configuration',
    category: 'Governance Controls',
    moduleId: 'scorecards',
    description: 'Maintain platform KPI definitions and structure from a governance-focused workspace.',
    requiredPermission: 'manage_kpis'
  },
  '/admin-reporting': {
    title: 'Reporting & Export',
    category: 'Governance Controls',
    moduleId: 'reports',
    description: 'Review lightweight governance reporting across approvals, delegations, and goal lifecycle activity, then export the current scope when needed.'
  },
  '/admin-tenant-configuration': {
    title: 'Tenant Configuration',
    category: 'Governance Controls',
    moduleId: 'administration',
    description: 'Manage tenant branding, terminology, and licensed module enablement.'
  }
};

export const NAVIGATION_BY_ROLE = {
  employee: [{ label: 'Self Service', items: [{ path: '/time-clock', label: 'Time Clock' }] }],
  supervisor: [{ label: 'Self Service', items: [{ path: '/time-clock', label: 'Time Clock' }] }],
  ops_manager: [
    {
      label: '',
      items: [
        { path: '/today', label: 'Today' },
        { path: '/my-dashboard', label: 'My Dashboard' },
        { path: '/location-scorecard', label: 'View Scorecard' },
        { path: '/goals', label: 'View Goals' },
        { path: '/station-summary', label: 'Location Summary' },
        { path: '/requests-calendar', label: 'Manage Time Off' },
        { path: '/attendance', label: 'Attendance' },
        { path: '/timecards', label: 'Timecards' },
        { path: '/timekeeping/reports', label: 'Recorded Hours' }
      ]
    }
  ],
  airport_manager: [
    {
      label: '',
      items: [
        { path: '/today', label: 'Today' },
        { path: '/station-dashboard', label: 'Location Overview' },
        { path: '/location-scorecard', label: 'View Scorecard' },
        { path: '/goals', label: 'View Goals' },
        { path: '/ops-managers', label: 'Team Directory' },
        { path: '/requests-calendar', label: 'Requests' },
        { path: '/attendance', label: 'Attendance' },
        { path: '/timecards', label: 'Timecards' },
        { path: '/timekeeping/reports', label: 'Recorded Hours' }
      ]
    }
  ],
  director: [
    {
      label: 'Command Center',
      items: [
        { path: '/today', label: 'Today' },
        { path: '/executive', label: 'Executive Snapshot' },
        { path: '/alerts', label: 'Alerts Center' },
        { path: '/requests-calendar', label: 'Requests' },
        { path: '/director-dashboard', label: 'Overview' },
        { path: '/attendance', label: 'Attendance' },
        { path: '/timecards', label: 'Timecards' },
        { path: '/timekeeping/reports', label: 'Recorded Hours' }
      ]
    },
    {
      label: 'Performance',
      items: [
        { path: '/location-scorecard', label: 'View Scorecard' },
        { path: '/goals/plan', label: 'Goal Plan' },
        { path: '/goals', label: 'Goals & Performance' },
        { path: '/kpis', label: 'Configure KPIs' }
      ]
    },
    {
      label: 'Workforce',
      // W6.3: single canonical entry in visible nav. The legacy
      // /employees nav item has been retired from the menu, but the
      // /employees/* aliases continue to resolve via
      // resolveWorkforceRoute, so external bookmarks and historical
      // links remain unaffected.
      items: [
        { path: '/workforce', label: 'Workforce' }
      ]
    },
    {
      label: 'Operations Setup',
      items: [
        { path: '/locations', label: 'Locations' },
        { path: '/timekeeping/admin', label: 'Timekeeping Administration' },
        { path: '/policies', label: 'Coverage Policies' },
        { path: '/integrations', label: 'Integrations' }
      ]
    },
    {
      label: 'Admin',
      items: [
        { path: '/audit', label: 'Audit Logs & Export' }
      ]
    }
  ],
  admin: [
    {
      label: 'Command Center',
      items: [
        { path: '/today', label: 'Today' },
        { path: '/executive', label: 'Executive Snapshot' },
        { path: '/alerts', label: 'Alerts Center' },
        { path: '/attendance', label: 'Attendance' },
        { path: '/timecards', label: 'Timecards' },
        { path: '/timekeeping/reports', label: 'Recorded Hours' }
      ]
    },
    {
      label: 'Governance Workspace',
      items: [
        { path: '/admin-workspace', label: 'Your workspace' }
      ]
    },
    {
      label: 'Workforce',
      // W6.3: single canonical entry in visible nav for admins.
      // Legacy /employees/* aliases continue to resolve.
      items: [
        { path: '/workforce', label: 'Workforce' }
      ]
    },
    {
      label: 'Admin',
      items: [{ path: '/timekeeping/admin', label: 'Timekeeping Administration' }]
    }
  ],
  super_admin: [
    {
      label: 'Platform',
      items: [
        { path: '/admin-workspace', label: 'Platform Workspace' }
      ]
    },
    {
      label: 'Platform Tools',
      items: [
        { path: '/admin-users', label: 'Platform Users' },
        { path: '/admin-reporting', label: 'Reporting & Export' }
      ]
    }
  ],
  platform_admin: [
    {
      label: 'Platform',
      items: [
        { path: '/admin-workspace', label: 'Platform Workspace' }
      ]
    },
    {
      label: 'Platform Tools',
      items: [
        { path: '/admin-users', label: 'Platform Users' },
        { path: '/admin-reporting', label: 'Reporting & Export' }
      ]
    }
  ]
};

export const STARTER_MODE_NAVIGATION = [
  {
    label: 'Starter Mode',
    items: [
      { path: '/setup', label: 'Setup Wizard' },
      { path: '/dashboard-preview', label: 'Dashboard Preview' },
      { path: '/locations', label: 'Locations' },
      { path: '/managers', label: 'Team' }
    ]
  }
];

export function getRouteMeta(path, fallbackPath = '/director-dashboard') {
  return APP_ROUTE_META[path] || APP_ROUTE_META[fallbackPath] || null;
}

/**
 * Scope-aware label for the "all locations the user can see" option.
 *
 *   accessAllLocations           → "Company Rollup"
 *   2+ assigned locations        → "Assigned Locations"
 *   0 or 1 assigned locations    → null  (no rollup option for this user)
 *
 * Callers should treat a null return as "do not show the rollup option" —
 * a single-location user's view should be pinned to their one location, and
 * a user with no scope at all has nothing to roll up.
 */
export function getScopeRollupLabel(user) {
  if (!user) return null;
  if (user.accessAllLocations) return 'Company Rollup';
  const assigned = Array.isArray(user.assignedLocationIds) ? user.assignedLocationIds : [];
  if (assigned.length > 1) return 'Assigned Locations';
  return null;
}

export function userHasScopeRollupOption(user) {
  return Boolean(getScopeRollupLabel(user));
}

export function canAccessRoute(user, path) {
  const routeMeta = APP_ROUTE_META[path];
  if (!routeMeta) {
    return true;
  }

  if (routeMeta.directorShellBypass && DIRECTOR_SHELL_ROLES.has(user?.role)) {
    return true;
  }

  const requiredPermission = routeMeta.requiredPermission;
  if (!requiredPermission) {
    return true;
  }

  if (!Array.isArray(user?.permissions)) {
    return false;
  }
  if (user.permissions.includes(requiredPermission)) {
    return true;
  }
  const fallbackPermissions = Array.isArray(routeMeta.fallbackPermissions) ? routeMeta.fallbackPermissions : [];
  return fallbackPermissions.some(permission => user.permissions.includes(permission));
}

export function resolveRouteModuleId(path) {
  if (!path) return null;
  if (APP_ROUTE_META[path]?.moduleId) {
    return APP_ROUTE_META[path].moduleId;
  }
  if (/^\/employees\/profile\/\d+$/.test(path)) return APP_ROUTE_META['/employees/profile/:managerId']?.moduleId || 'workforce';
  if (/^\/employees\/profile\/by-profile\/\d+$/.test(path)) return APP_ROUTE_META['/employees/profile/by-profile/:employeeProfileId']?.moduleId || 'workforce';
  if (/^\/workforce\/employees\/\d+$/.test(path)) return APP_ROUTE_META['/workforce/employees/:employeeProfileId']?.moduleId || 'workforce';
  return null;
}

export function getNavigationForRole(role, terminology = DEFAULT_TENANT_TERMINOLOGY) {
  const defaults = NAVIGATION_BY_ROLE[role] || [];
  if (terminology === DEFAULT_TENANT_TERMINOLOGY) {
    return defaults;
  }
  const resolved = mergeTenantTerminology(terminology);
  return defaults.map((group) => ({
    ...group,
    label: group.label === 'Workforce' ? resolved.workspace.workforce : group.label,
    items: (group.items || []).map((item) => {
      if (item.path === '/workforce' && item.label === 'Workforce') {
        return { ...item, label: resolved.workspace.workforce };
      }
      if (item.path === '/requests-calendar' && item.label === 'Requests') {
        return { ...item, label: resolved.workspace.requests };
      }
      if (item.path === '/locations' && item.label === 'Locations') {
        return { ...item, label: resolved.organization.locations };
      }
      if (item.path === '/station-summary' && item.label === 'Location Summary') {
        return { ...item, label: `${resolved.organization.location} Summary` };
      }
      if (item.path === '/station-dashboard' && item.label === 'Location Overview') {
        return { ...item, label: `${resolved.organization.location} Overview` };
      }
      return item;
    })
  }));
}

export function normalizeAppRole(role = '') {
  const normalized = String(role || '').trim().toLowerCase();
  return LEGACY_ROLE_MAP[normalized] || normalized;
}

export function getWorkspaceForUser(user) {
  const normalizedRole = normalizeAppRole(user?.role);
  return WORKSPACE_BY_ROLE[normalizedRole] || null;
}

export function isKnownWorkspaceUser(user) {
  return Boolean(getWorkspaceForUser(user));
}

export function formatWorkspaceLabel(workspace, terminology = DEFAULT_TENANT_TERMINOLOGY) {
  const resolved = mergeTenantTerminology(terminology);
  if (workspace === WORKSPACE_KEYS.ADMIN) {
    return 'Governance Workspace';
  }

  if (workspace === WORKSPACE_KEYS.DIRECTOR) {
    return 'Leadership Workspace';
  }

  if (workspace === WORKSPACE_KEYS.AIRPORT) {
    return `${resolved.organization.location} Workspace`;
  }

  if (workspace === WORKSPACE_KEYS.OPS) {
    return 'Assigned Workspace';
  }

  return 'Workspace';
}

export function getUserDisplayName(user) {
  return user?.managerName || user?.email || 'Authenticated user';
}

export function getUserReferenceTitle(user) {
  return user?.roleTitle || user?.managerRoleTitle || '';
}

/**
 * Client-side permission check — mirrors server/middleware/roles.js hasPermission.
 * Use this instead of comparing user.role to a string.
 */
export function userHasPermission(user, permissionKey) {
  return Array.isArray(user?.permissions) && user.permissions.includes(permissionKey);
}

/**
 * Map the universal three-tier model to internal workspace keys.
 * Pass a user object — the function reads both the new tier fields and
 * the legacy role field so both models work during the migration.
 */
export function getTierLabel(user) {
  const role = normalizeAppRole(user?.role);
  if (role === 'director') return 'Super User';
  if (role === 'admin') return 'Admin';
  return 'User';
}
